import React from 'react';
import {
    Card, CardTitle, Button, Pill, EmptyState, Notice,
    DataTable, TableScroll, FieldLabel,
} from '../ui';
import {
    MonoCell, Muted, DetailMeta,
    Description, DescriptionSections, RecoList, RecoItem, TargetVersion,
} from './security.styled';
import { SeverityPill, formatEpss, formatDate } from './severity';
import PathCell from './pathCell';
import { downloadCsv } from './csvDownload';

// Un fichier par CVE, une ligne par appareil exposé. La mécanique du
// téléchargement est partagée avec l'export de la fiche appareil : deux
// implémentations des mêmes conventions Excel finiraient par diverger.
function exportDevicesCsv(detail) {
    downloadCsv(
        `${detail.cveCode}_appareils.csv`,
        [
            'CVE', 'Appareil', 'Groupe RBAC', 'Sévérité', 'Logiciels',
            'Chemins disque', 'Chemins registre', 'Correctif recommandé',
        ],
        detail.devices.map((device) => [
            detail.cveCode,
            device.hostname || '',
            device.rbacGroup || '',
            device.severity || '',
            (device.software || []).join(' | '),
            (device.diskPaths || []).join(' | '),
            (device.registryPaths || []).join(' | '),
            device.recommendedSecurityUpdate || '',
        ])
    );
}

// Fiche complète d'une CVE : caractéristiques, puis un tableau une ligne par
// appareil avec ses chemins.
export default function CveDetailView({ detail }) {
    const notOnEstate = detail.affectedCount === 0;

    // Le correctif n'est renseigné que sur 9 % des relations (produits Microsoft
    // et paquets Linux essentiellement) : on masque la colonne quand aucun
    // appareil n'en a, plutôt que d'afficher une colonne de tirets.
    // Elle reste indispensable là où elle est remplie : 334 CVE ont un correctif
    // DIFFÉRENT selon le poste, ce que la section Remédiation — qui raisonne par
    // produit et non par appareil — ne peut pas exprimer.
    const showUpdate = detail.devices?.some((device) => device.recommendedSecurityUpdate);

    return (
        <>
            <Card>
                <CardTitle>Caractéristiques</CardTitle>
                <DetailMeta style={{ marginTop: 0, borderTop: 'none', paddingTop: 0 }}>
                    <div>
                        <div className="label">Sévérité</div>
                        <div className="value"><SeverityPill severity={detail.severity} /></div>
                    </div>
                    <div>
                        <div className="label">CVSS</div>
                        <div className="value">{detail.cvssScore ?? '—'}</div>
                    </div>
                    <div>
                        <div className="label">EPSS</div>
                        <div className="value">{formatEpss(detail.epssScore)}</div>
                    </div>
                    <div>
                        <div className="label">Exploit</div>
                        <div className="value">
                            {detail.exploitabilityLevel === 'PoC'
                                ? <Pill $tone="danger">Disponible</Pill>
                                : <Muted>Aucun connu</Muted>}
                        </div>
                    </div>
                    <div>
                        <div className="label">Publiée le</div>
                        <div className="value">{formatDate(detail.publishedOn)}</div>
                    </div>
                    <div>
                        <div className="label">Appareils exposés</div>
                        <div className="value">{detail.affectedCount}</div>
                    </div>
                </DetailMeta>

                {detail.rbacGroups && (
                    <div style={{ marginTop: '14px' }}>
                        <FieldLabel as="div">Groupes touchés</FieldLabel>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                            {detail.rbacGroups.split(';').filter(Boolean).map((group) => (
                                <Pill key={group} $tone="neutral">{group}</Pill>
                            ))}
                        </div>
                    </div>
                )}
            </Card>

            {detail.description && (
                <Card>
                    <CardTitle>Description</CardTitle>

                    {/* Sections titrées quand le serveur a su découper le résumé —
                        12 939 CVE sur 14 844 en portent les quatre. Le paragraphe
                        brut reste le repli : mieux vaut un pavé qu'un vide si
                        Microsoft change son gabarit. */}
                    {detail.descriptionSections?.length > 0 ? (
                        <DescriptionSections>
                            {detail.descriptionSections.map((section) => (
                                <div key={section.cle}>
                                    {section.titre && (
                                        <FieldLabel as="div" style={{ marginBottom: '4px' }}>
                                            {section.titre}
                                        </FieldLabel>
                                    )}
                                    <Description>{section.corps}</Description>
                                </div>
                            ))}
                        </DescriptionSections>
                    ) : (
                        <Description>{detail.description}</Description>
                    )}
                    {/* Mention conservée et rendue explicite plutôt que noyée en
                        fin de paragraphe : ces résumés sont générés par Microsoft
                        et ne font pas autorité pour décider d'une remédiation. */}
                    {detail.descriptionAiGenerated && (
                        <Muted style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '10px', fontSize: '0.72rem' }}>
                            <i className="bx bx-bot" />
                            Résumé généré par IA côté Microsoft — à recouper avec l'avis de l'éditeur
                            avant toute décision.
                        </Muted>
                    )}
                </Card>
            )}

            {detail.recommendations?.length > 0 && (
                <Card>
                    <CardTitle>Remédiation</CardTitle>
                    <RecoList>
                        {detail.recommendations.map((reco) => {
                            // Une recommandation qui indique une version cible est
                            // directement actionnable — c'est le seul cas où l'on
                            // sait quoi faire sans investigation supplémentaire.
                            const actionable = Boolean(reco.recommendedVersion);
                            return (
                                <RecoItem key={reco.id} $actionable={actionable}>
                                    <i className={actionable ? 'bx bx-up-arrow-circle' : 'bx bx-info-circle'} />
                                    <div className="body">
                                        <div className="name">{reco.recommendationName}</div>
                                        <div className="meta">
                                            {reco.weaknesses > 0 && (
                                                <span>{reco.weaknesses} CVE couverte{reco.weaknesses > 1 ? 's' : ''}</span>
                                            )}
                                            {reco.exposedMachinesCount > 0 && (
                                                <span>{reco.exposedMachinesCount} appareil{reco.exposedMachinesCount > 1 ? 's' : ''} exposé{reco.exposedMachinesCount > 1 ? 's' : ''}</span>
                                            )}
                                            {reco.recommendationCategory && <span>{reco.recommendationCategory}</span>}
                                            {reco.hasUnpatchableCve && (
                                                <Pill $tone="warn">Certaines CVE sans correctif</Pill>
                                            )}
                                        </div>
                                    </div>
                                    {actionable && <TargetVersion>{reco.recommendedVersion}</TargetVersion>}
                                </RecoItem>
                            );
                        })}
                    </RecoList>
                </Card>
            )}

            {notOnEstate ? (
                <Card>
                    {/* Une CVE connue du catalogue mais absente du parc est une
                        bonne nouvelle : ton "ok", pas un état vide neutre. */}
                    <Notice $tone="ok">
                        <i className="bx bx-check-shield" />
                        Cette vulnérabilité n'est présente sur aucun appareil du parc.
                    </Notice>
                </Card>
            ) : (
                <Card $pad="16px">
                    <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        Appareils impactés ({detail.affectedCount})
                        <Button
                            $variant="secondary"
                            onClick={() => exportDevicesCsv(detail)}
                            style={{ marginLeft: 'auto', fontWeight: 700 }}
                        >
                            <i className="bx bxs-download" />
                            Exporter en CSV
                        </Button>
                    </CardTitle>

                    {detail.devices.length === 0 ? (
                        <EmptyState>
                            <i className="bx bx-check-shield" />
                            <h3>Aucun appareil</h3>
                            <p>Aucun appareil exposé à cette vulnérabilité.</p>
                        </EmptyState>
                    ) : (
                        <TableScroll>
                            <DataTable>
                                <thead>
                                    <tr>
                                        <th>Appareil</th>
                                        <th>Groupe</th>
                                        <th>Logiciel</th>
                                        <th>Chemins disque</th>
                                        <th>Chemins registre</th>
                                        {showUpdate && <th>Correctif</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {detail.devices.map((device) => (
                                        <tr key={device.deviceId}>
                                            <td>{device.hostname || <Muted>Inconnu</Muted>}</td>
                                            <td><Muted>{device.rbacGroup || '—'}</Muted></td>
                                            <td><Muted>{(device.software || []).join(', ') || '—'}</Muted></td>
                                            <td><PathCell paths={device.diskPaths} /></td>
                                            <td><PathCell paths={device.registryPaths} /></td>
                                            {showUpdate && (
                                                <td>
                                                    {device.recommendedSecurityUpdate
                                                        ? <MonoCell>{device.recommendedSecurityUpdate}</MonoCell>
                                                        : <Muted>—</Muted>}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </DataTable>
                        </TableScroll>
                    )}
                </Card>
            )}
        </>
    );
}
